class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.viewAllTerms = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.input = this.querySelector('input[type="search"]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector('details.search_details')
    this.isOpen = false;
    this.typoDictionary = this.buildTypoDictionary(this.dataset.typoDictionary || '');
    this.typoLabel = this.dataset.typoLabel || 'Showing results for';
    this.posProductExclusion = '-tag:consignr-pos-product';

    this.setupEventListeners();
  }
    
  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));
    
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 500).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));
    this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
  }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const newSearchTerm = this.getQuery();
      
      if (!newSearchTerm.length) {
        this.close(true);
        return;
      }
      
      this.getSearchResults(newSearchTerm);
    }
  
    onFormSubmit(event) {
      const term = this.getQuery();
      if (!term.length || this.querySelector('[aria-selected="true"] a')) {
        event.preventDefault();
        return;
      }

      // Keep the customer-entered term in the field, but submit Shopify's
      // product-level exclusion so POS-only ConsignR records never reach the
      // storefront results page.
      event.preventDefault();
      const form = event.currentTarget;
      const params = new URLSearchParams(new FormData(form));
      params.set('q', this.withPosProductExclusion(term));
      window.location.assign(`${form.action}?${params.toString()}`);
    }

    withPosProductExclusion(term) {
      const normalized = term.trim();
      if (!normalized || normalized.includes(this.posProductExclusion)) return normalized;
      return `${normalized} ${this.posProductExclusion}`;
    }
  
    onFocus() {
      const newSearchTerm = this.getQuery();
      
      if (!newSearchTerm.length) return this.showTrendingAndProducts();

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(newSearchTerm);
      }
    }
  
    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.close();
          this.hideTrendingAndProducts();
        };
      })
    }
    
    onKeyup(event) {
      if (!this.getQuery().length) {
        this.close(true);
        this.showTrendingAndProducts();
      };
      event.preventDefault();
      
      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up')
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }
  
    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (
        event.code === 'ArrowUp' ||
        event.code === 'ArrowDown'
      ) {
        event.preventDefault();
      }
    }
  
    switchOption(direction) {
      if (!this.getAttribute('open')) return;
  
      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');
      const allElements = this.querySelectorAll('li');
      let activeElement = this.querySelector('li');
  
      if (moveUp && !selectedElement) return;
  
      this.statusElement.textContent = '';
      
      if (!moveUp && selectedElement) {
        activeElement = selectedElement.nextElementSibling || allElements[0];
      } else if (moveUp) {
        activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
      }
  
      if (activeElement === selectedElement) return;
  
      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);
  
      this.setLiveRegionText(activeElement.textContent);
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }
      
    selectOption() {
      const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
  
      if (selectedProduct) selectedProduct.click();
    }
    
    getSearchResults(newSearchTerm) {
      const queryKey = newSearchTerm.replace(" ", "-").toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        this.updateViewAllLink(this.viewAllTerms[queryKey] || newSearchTerm);
        return;
      }

      this.fetchResultsMarkup(newSearchTerm)
        .then(async (resultsMarkup) => {
          let finalMarkup = resultsMarkup;
          let finalTerm = newSearchTerm;

          // No products for the typed term — try a typo correction against the
          // brand/type dictionary (predictive search has no typo tolerance,
          // unlike the full /search page).
          if (resultsMarkup.indexOf('products-grid') === -1) {
            const corrected = this.correctTypo(newSearchTerm);
            if (corrected) {
              const correctedMarkup = await this.fetchResultsMarkup(corrected);
              if (correctedMarkup.indexOf('products-grid') !== -1) {
                const note = `<div class="predictive-search__typo-note">${this.typoLabel} <strong>${this.escapeHtml(corrected)}</strong></div>`;
                finalMarkup = correctedMarkup.indexOf('</h2>') !== -1
                  ? correctedMarkup.replace('</h2>', '</h2>' + note)
                  : note + correctedMarkup;
                finalTerm = corrected;
              }
            }
          }

          this.cachedResults[queryKey] = finalMarkup;
          this.viewAllTerms[queryKey] = finalTerm;
          this.renderSearchResults(finalMarkup);
          this.updateViewAllLink(finalTerm);
        })
        .catch((error) => {
          this.close();
          throw error;
        });
    }

    fetchResultsMarkup(term) {
      const shopifyTerm = this.withPosProductExclusion(term);
      return fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(shopifyTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=${this.productToShow}&section_id=predictive-search`)
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }

          return response.text();
        })
        .then((text) => new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML);
    }

    buildTypoDictionary(raw) {
      const words = new Set();
      raw.toLowerCase().split(',').forEach((entry) => {
        entry = entry.trim();
        if (!entry) return;
        if (entry.length >= 3) words.add(entry);
        entry.split(/[\s\-]+/).forEach((part) => {
          if (part.length >= 4) words.add(part);
        });
      });
      return Array.from(words);
    }

    // Optimal string alignment distance (Levenshtein + adjacent transposition),
    // capped at `max` for speed.
    osaDistance(a, b, max) {
      if (Math.abs(a.length - b.length) > max) return max + 1;
      let prev2 = null;
      let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
      for (let i = 1; i <= a.length; i++) {
        const cur = [i];
        let rowMin = i;
        for (let j = 1; j <= b.length; j++) {
          let d = Math.min(
            prev[j] + 1,
            cur[j - 1] + 1,
            prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
          );
          if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
            d = Math.min(d, prev2[j - 2] + 1);
          }
          cur[j] = d;
          if (d < rowMin) rowMin = d;
        }
        if (rowMin > max) return max + 1;
        prev2 = prev;
        prev = cur;
      }
      return prev[b.length];
    }

    correctTypo(term) {
      if (!this.typoDictionary.length) return null;
      const words = term.toLowerCase().split(/\s+/);
      let changed = false;
      const corrected = words.map((word) => {
        if (word.length < 4 || this.typoDictionary.indexOf(word) !== -1) return word;
        const maxDistance = word.length >= 6 ? 2 : 1;
        let best = null;
        let bestDistance = maxDistance + 1;
        for (const candidate of this.typoDictionary) {
          const distance = this.osaDistance(word, candidate, maxDistance);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = candidate;
            if (distance === 1) break;
          }
        }
        if (best) changed = true;
        return best || word;
      });
      return changed ? corrected.join(' ') : null;
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
    
    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
      
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }
  
    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;
      
      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);

      this.convertResultPrices();
      this.setLiveRegionResults();
      this.open();
      this.hideTrendingAndProducts();
    }

    // Nove vlozene ceny jsou v mene obchodu - konvertor uz dobehl pri nacteni
    // stranky. Stejny vzor jako collection-filters-form.js a product-bundle.js.
    convertResultPrices() {
      if (typeof Currency === 'undefined' || typeof Currency.convertAll !== 'function') return;
      if (!window.shop_currency) return;

      var target = null;
      if (window.jQuery) target = jQuery('#currencies .active').attr('data-currency');
      if (!target) target = Currency.currentCurrency;
      if (!target || target === window.shop_currency) return;

      Currency.convertAll(window.shop_currency, target, 'span.money', 'money_format');
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }
    
    getResultsMaxHeight() {
      this.resultsMaxHeight = window.innerHeight - document.querySelector('[id^="shopify-section-header"]').getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }
    
    open() {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;   
      this.searchDetails.setAttribute('open', true);
    }

    close(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      
      const selected = this.querySelector('[aria-selected="true"]');
  
      if (selected) selected.setAttribute('aria-selected', false);
  
      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false
      this.predictiveSearchResults.removeAttribute('style');
  
      this.isOpen = false;
      this.searchDetails.removeAttribute('open', true);
    }

    showTrendingAndProducts() {
      if (!this.trendingAndProductsBlock) return 
      this.trendingAndProductsBlock.classList.add('is-show')
      this.trendingAndProductsBlock.classList.remove('hidden')
    }

    hideTrendingAndProducts() {
      if (!this.trendingAndProductsBlock) return 

      this.trendingAndProductsBlock.classList.remove('is-show')
      this.trendingAndProductsBlock.classList.add('hidden')
    }

    onDocClick(e) {
      let closeButton;
      if (e.target.matches('.header-search-popup-close') || e.target.matches('.header-search-close')) {
        closeButton = e.target 
      } else if (e.target.closest('.header-search-popup-close')) {
        closeButton = e.target.closest('.header-search-popup-close')
      } else if (e.target.closest('.header-search-close')) {
        closeButton = e.target.closest('.header-search-close')
      } 

      const isInModal = this.contains(e.target) 

      if (isInModal && !closeButton) return;

      this.close();
      this.hideTrendingAndProducts();
    }

    onCloseStickySearchClick() {
      this.close();
      this.hideTrendingAndProducts();
      document.querySelector('body').classList.remove('sticky-search-open')
    }

    updateViewAllLink(newSearchTerm) {
      const qsViewAllLink = this.querySelector('[data-qs-view-all-link]');
      if (!qsViewAllLink) return 

      const linkTotal = `${routes.search_url}?q=${encodeURIComponent(this.withPosProductExclusion(newSearchTerm))}&type=product`
      
      qsViewAllLink.href = linkTotal
      return this.getTotalResults(linkTotal).then(count => {
        const countLabel = qsViewAllLink.querySelector('[data-qs-result-count]');
        if (qsViewAllLink.isConnected && countLabel && count !== null) {
          countLabel.textContent = `(${count})`;
          countLabel.hidden = false;
        }
      });
    }

    getTotalResults(url) {
      return fetch(url)
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }

          return response.text();
        })  
        .then((text) => {
          const totalCount = new DOMParser().parseFromString(text, 'text/html').querySelector('[id^="SearchSection"]')?.dataset.searchCount;
          return /^\d+$/.test(totalCount || '') ? Number(totalCount) : null;
        })
        // The button stays useful even when the optional total cannot be loaded.
        .catch(() => null);
    }
  }
  
  customElements.define('predictive-search', PredictiveSearch);
